import classnames from 'classnames2';
import React from 'react';

import { vizColors } from '@opengov/capital-style';

import { Icon } from '../Icon';
import { IconSVGs } from '../Icon/IconSVGs';
import { IBaseProps } from '../utils';

import styles from './Badge.module.scss';

export type BadgeVariant = 'neutral' | 'warning' | 'bright' | 'strong' | 'positive' | 'negative';
export interface ISelectBadgeProps extends IBaseProps {
  /** Defines the string to be displayed within the badge */
  text: string;
  /** Sets a max number of characters to display in the badge. */
  maxLength?: number;
  /** Defines the style variant of the badge */
  variant?: BadgeVariant;
  /** Optional icon at the left side of the badge */
  icon?: keyof typeof IconSVGs;
}

/**
 * Use `Badge` to highlight key info with a predefined status. Easy peasy!
 */
export const Badge: React.FC<ISelectBadgeProps> = ({
  className,
  text = '',
  variant = 'neutral',
  maxLength,
  icon,
  dataTest
}) => {
  let badgeText = text;
  // using an inline style to apply color tokens that are not exposed in CSS.
  let inlineStyle = {};
  if (variant === 'positive') {
    inlineStyle = { backgroundColor: vizColors.jade[700] };
  } else if (variant === 'negative') {
    inlineStyle = { backgroundColor: vizColors.red[700] };
  } else if (variant === 'warning') {
    inlineStyle = { backgroundColor: vizColors.orange[200] };
  }

  if (badgeText && badgeText.length > maxLength) {
    badgeText = `${text.slice(0, maxLength - 1)}...`;
  }

  return (
    <span
      className={classnames(styles.badge, className, styles[variant])}
      style={inlineStyle}
      data-test={dataTest}
      title={text}
    >
      {icon && <Icon icon={icon} className={styles.icon} />}
      {badgeText}
    </span>
  );
};
